import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout, ClairName } from '../components/layout/DashboardLayout';
import { dashboardAPI, contentAPI, onboardingAPI } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Target, BookOpen, PenLine, CheckCircle2, ArrowRight, Lock, MessageCircle, FileText, Video, Headphones, GraduationCap, Sparkles, Clock, CalendarClock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WelcomeModal } from '../components/onboarding/WelcomeModal';
import { GettingStartedChecklist } from '../components/onboarding/GettingStartedChecklist';

const TYPE_ICONS = {
  article: FileText,
  worksheet: BookOpen,
  video: Video,
  audio: Headphones,
  course: GraduationCap
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(true);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  
  // Onboarding state
  const [showWelcome, setShowWelcome] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [checklistCollapsed, setChecklistCollapsed] = useState(false);

  // Content Library is available for guided_path and personal_path
  const hasContentAccess = user?.subscription_tier === 'guided_path' || user?.subscription_tier === 'personal_path';

  const loadStats = useCallback(async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecommendations = useCallback(async () => {
    try {
      const response = await contentAPI.getRecommendations();
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setRecsLoading(false);
    }
  }, []);

  const loadUpcomingSessions = useCallback(async () => {
    try {
      const response = await dashboardAPI.getUpcomingSessions();
      setUpcomingSessions(response.data || []);
    } catch (error) {
      console.error('Failed to load upcoming sessions:', error);
    }
  }, []);

  const loadOnboardingStatus = useCallback(async () => {
    try {
      const response = await onboardingAPI.getStatus();
      setOnboardingStatus(response.data);
      
      // Show welcome modal if onboarding should be shown and welcome not yet seen
      if (response.data.show_onboarding && !response.data.steps?.welcome_seen) {
        setShowWelcome(true);
      }
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
    }
  }, []);

  const handleWelcomeClose = async () => {
    setShowWelcome(false);
    // Mark welcome as seen
    try {
      await onboardingAPI.updateStep('welcome_seen');
      setOnboardingStatus(prev => ({
        ...prev,
        steps: { ...prev?.steps, welcome_seen: true }
      }));
    } catch (error) {
      console.error('Failed to mark welcome as seen:', error);
    }
  };

  const handleStartJourney = async () => {
    await handleWelcomeClose();
  };

  const handleDismissOnboarding = async () => {
    try {
      await onboardingAPI.dismiss();
      setOnboardingStatus(prev => ({ ...prev, show_onboarding: false, onboarding_dismissed: true }));
    } catch (error) {
      console.error('Failed to dismiss onboarding:', error);
    }
  };

  useEffect(() => {
    loadStats();
    loadRecommendations();
    loadOnboardingStatus();
    loadUpcomingSessions();
  }, [loadStats, loadRecommendations, loadOnboardingStatus, loadUpcomingSessions]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading your dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Welcome Modal for new users */}
      <WelcomeModal 
        isOpen={showWelcome}
        onClose={handleWelcomeClose}
        userName={user?.name}
        onStartJourney={handleStartJourney}
      />

      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif mb-2">Your Journey Dashboard</h1>
          <p className="text-muted-foreground">Track your progress and continue growing</p>
        </div>

        {/* Getting Started Checklist - Show if onboarding not completed/dismissed */}
        {onboardingStatus?.show_onboarding && (
          <GettingStartedChecklist 
            steps={onboardingStatus.steps}
            onDismiss={handleDismissOnboarding}
            isCollapsed={checklistCollapsed}
            onToggleCollapse={() => setChecklistCollapsed(!checklistCollapsed)}
          />
        )}

        {/* Upcoming Session Reminder */}
        {upcomingSessions.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20 p-5" data-testid="upcoming-session-banner">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                <CalendarClock size={20} className="text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm mb-1">Upcoming Session</h3>
                {upcomingSessions.slice(0, 2).map((session) => (
                  <div key={session.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{session.title}</span>
                    <span>·</span>
                    <span>{session.group_name}</span>
                    <span>·</span>
                    <span>{new Date(session.date_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {new Date(session.date_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/groups')}
                className="text-accent hover:text-accent/80 flex-shrink-0"
              >
                View <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Journal Entries - Hero position */}
          <Card className="p-6 rounded-2xl border-border shadow-md" data-testid="stats-journal-entries">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Journal Entries</p>
                <p className="text-3xl font-serif text-foreground">{stats?.journal_entries || 0}</p>
              </div>
              <PenLine className="text-accent" size={24} />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border-border shadow-md" data-testid="stats-goals-completed">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Goals Completed</p>
                <p className="text-3xl font-serif text-foreground">{stats?.goals_completed || 0}</p>
              </div>
              <CheckCircle2 className="text-accent" size={24} />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border-border shadow-md" data-testid="stats-active-goals">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Active Goals</p>
                <p className="text-3xl font-serif text-foreground">{stats?.active_goals || 0}</p>
              </div>
              <Target className="text-primary" size={24} />
            </div>
          </Card>

          {/* Lessons Completed - Grayed out if no access */}
          <Card 
            className={`p-6 rounded-2xl border-border shadow-md ${!hasContentAccess ? 'opacity-50' : ''}`} 
            data-testid="stats-lessons"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  Lessons Completed
                  {!hasContentAccess && <Lock size={12} className="inline ml-1" />}
                </p>
                <p className="text-3xl font-serif text-foreground">{stats?.lessons_completed || 0}</p>
              </div>
              <BookOpen className={hasContentAccess ? "text-primary" : "text-muted-foreground"} size={24} />
            </div>
          </Card>
        </div>

        {/* Quick Actions - Reordered: Journal, Clair, Content */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Write in Journal - Primary action */}
          <Card 
            className="p-8 rounded-2xl border-border bg-accent/15 shadow-md hover:-translate-y-1 hover:shadow-lg duration-300 cursor-pointer"
            onClick={() => navigate('/journal')}
            data-testid="quick-action-journal"
          >
            <div className="flex items-center gap-3 mb-2">
              <PenLine className="text-accent" size={24} />
              <h3 className="text-xl font-serif">Write in Journal</h3>
            </div>
            <p className="text-sm text-muted-foreground">Reflect on your day and insights</p>
          </Card>

          {/* Talk to Clair - Center */}
          <Card 
            className="p-8 rounded-2xl border-border bg-accent/15 shadow-md hover:-translate-y-1 hover:shadow-lg duration-300 cursor-pointer"
            onClick={() => navigate('/coach')}
            data-testid="quick-action-coach"
          >
            <div className="flex items-center gap-3 mb-2">
              <MessageCircle className="text-primary" size={24} />
              <h3 className="text-xl font-serif">Talk to <ClairName /></h3>
            </div>
            <p className="text-sm text-muted-foreground">A space to talk things through</p>
          </Card>

          {/* Explore Content - Clickable for upgrade */}
          <Card 
            className={`p-8 rounded-2xl border-border shadow-md hover:-translate-y-1 hover:shadow-lg duration-300 cursor-pointer ${
              hasContentAccess ? 'bg-accent/15' : 'bg-muted/30'
            }`}
            onClick={() => navigate('/content')}
            data-testid="quick-action-content"
          >
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className={hasContentAccess ? "text-primary" : "text-muted-foreground"} size={24} />
              <h3 className={`text-xl font-serif ${!hasContentAccess ? 'text-muted-foreground' : ''}`}>
                Explore Content
                {!hasContentAccess && <Lock size={14} className="inline ml-2" />}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {hasContentAccess ? 'Discover lessons and exercises' : 'Unlock with Guided Path'}
            </p>
          </Card>
        </div>

        {/* Admin Quick Action - Content Studio */}
        {user?.is_admin && (
          <Card 
            className="p-6 rounded-2xl border-primary/30 bg-primary/5 shadow-md hover:-translate-y-1 hover:shadow-lg duration-300 cursor-pointer"
            onClick={() => navigate('/content-studio')}
            data-testid="admin-content-studio"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="text-primary" size={24} />
                <div>
                  <h3 className="text-lg font-medium">CLaiR Content Studio</h3>
                  <p className="text-sm text-muted-foreground">Generate lessons with AI</p>
                </div>
              </div>
              <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">Admin</span>
            </div>
          </Card>
        )}

        {/* Recommended For You - Only show if user has content access and recommendations exist */}
        {hasContentAccess && !recsLoading && recommendations.length > 0 && (
          <Card className="p-8 rounded-2xl border-border" data-testid="recommended-for-you">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Sparkles className="text-primary" size={24} />
                <h2 className="text-2xl font-serif">Recommended for You</h2>
              </div>
              <Button 
                variant="outline"
                onClick={() => navigate('/content')} 
                className="rounded-full"
                data-testid="browse-library-btn"
              >
                Browse Library
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {recommendations.map((item, index) => {
                const TypeIcon = TYPE_ICONS[item.content_type] || FileText;
                return (
                  <div
                    key={item.id || `rec-${index}`}
                    className="group p-4 rounded-xl bg-muted/30 hover:bg-muted/50 cursor-pointer transition-all hover:-translate-y-1"
                    onClick={() => navigate('/content', { state: { openContent: item.id } })}
                    data-testid={`recommendation-${index}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <TypeIcon size={16} className="text-primary" />
                      <span className="text-xs text-muted-foreground capitalize">{item.content_type}</span>
                    </div>
                    <h3 className="font-medium text-sm mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {item.description}
                    </p>
                    {item.duration_minutes && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock size={12} />
                        <span>{item.duration_minutes} min</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Goals Summary Card - Links to Goals Page */}
        <Card className="p-8 rounded-2xl border-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif">Your Goals</h2>
            <Button 
              onClick={() => navigate('/goals')} 
              className="rounded-full"
              data-testid="view-goals-btn"
            >
              View All Goals
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>

          {stats?.recent_goals && stats.recent_goals.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_goals.slice(0, 3).map((goal, index) => (
                <div 
                  key={goal.id} 
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate('/goals')}
                  data-testid={`goal-preview-${index}`}
                >
                  <div className="flex items-center gap-3">
                    {goal.completed ? (
                      <CheckCircle2 className="text-accent flex-shrink-0" size={20} />
                    ) : (
                      <Target className="text-primary flex-shrink-0" size={20} />
                    )}
                    <div>
                      <h3 className="font-medium">{goal.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {goal.completed ? 'Completed' : `${goal.progress || 0}% progress`}
                      </p>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-muted-foreground" />
                </div>
              ))}
              
              {stats.recent_goals.length > 3 && (
                <p className="text-center text-sm text-muted-foreground pt-2">
                  +{stats.recent_goals.length - 3} more goals
                </p>
              )}
            </div>
          ) : (
            <div 
              className="text-center py-12 cursor-pointer hover:bg-muted/30 rounded-xl transition-colors"
              onClick={() => navigate('/goals')}
              data-testid="no-goals-message"
            >
              <Target className="mx-auto mb-4 opacity-50 text-muted-foreground" size={48} />
              <p className="text-muted-foreground mb-2">No goals yet</p>
              <p className="text-sm text-primary">Click to create your first goal</p>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
